import { useUser } from '@auth0/nextjs-auth0';
import React, { FC, useEffect, useState } from 'react';
import {
  Edition,
  EditionLabels,
  CrmType,
  CrmLabels,
  CrmAuthSlugs,
} from '../../utils/enums';
import { UserInfo } from '../../utils/interfaces';
import { BiPencil, BiSave } from 'react-icons/bi';
import { FaInfoCircle, FaMicrosoft } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { FiLink } from 'react-icons/fi';
import { Field, FieldProps, Form, Formik } from 'formik';
import {
  extractLinkedInUsername,
  buildLinkedInUrl,
  isLinkedInUrl,
} from '../../utils/helpers/linkedInHelper';
import {
  canUpgradeToUltra,
  getUltraCheckoutPath,
  ULTRA_UPGRADE_LABEL,
} from '../../utils/helpers/ultraCheckout';
import {
  TimeZone,
  UserPreferences,
  Tokens,
  AddExternalCalendarRequest,
  ExternalCalendar,
  CalendarListExternalCalendar,
  CrmConnectionInfo,
  CrmAuthRequest,
  CrmAuthResponse,
} from '../../utils/types';
import useSWR from 'swr';

import {
  Select,
  Option,
  Button,
  DialogHeader,
  Dialog,
  DialogBody,
  Typography,
  Input,
  DialogFooter,
  Accordion,
  AccordionHeader,
  AccordionBody,
} from '@material-tailwind/react';
import TZComboBox from '../shared/comboBoxes/TZComboBox';
import Link from 'next/link';
import Toast from '../shared/toast';
import { getTokensFromNewMicrosoftSignIn } from '../../utils/auth/getTokensFromNewMicrosoftSignIn';
import { getTokensFromNewGoogleSignIn } from '../../utils/auth/getTokensFromNewGoogleSignIn';
import * as Sentry from '@sentry/nextjs';
import CalendarList from '../shared/smallComponents/CalendarList';
import { CgProfile } from 'react-icons/cg';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ClipLoader } from 'react-spinners';
import { EmailLink, PhoneLink } from '../shared/ContactLinks';
import { CrmConnectionNameModal } from '../shared/CrmConnectionNameModal';
import DeleteModal from './deleteModal';

type Props = {
  timeZones: TimeZone[];
  classicUrl: string;
};
interface SubscriberInfo {
  subscriberId: number;
  userName: string;
  userId: string;
  email: string;
  crmEmail: string | null;
  firstName: string;
  lastName: string;
  company: string | null;
  cellPhone: string | null;
  title: string | null;
  additionalPhone: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  linkedInUrl: string | null;
}
const CrmConnectionTypeRow: React.FC<{
  crmType: CrmType;
  connections: CrmConnectionInfo[];
  isLoading: boolean;
  onConnect: () => void;
  onDisconnect: (connection: CrmConnectionInfo) => void;
}> = ({ crmType, connections, isLoading, onConnect, onDisconnect }) => {
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    number | null
  >(null);

  const isConnected = connections.length > 0;
  const effectiveConnectionId =
    selectedConnectionId ?? connections[0]?.crmConnectionId ?? null;
  const selectedConnection =
    connections.find((c) => c.crmConnectionId === effectiveConnectionId) ??
    null;

  return (
    <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
      <div className="sm:w-1/4 font-bold text-gray-700 text-left">
        <span>{CrmLabels[crmType]}</span>
      </div>
      <div className="flex-1 flex flex-wrap items-center gap-3">
        {connections.length > 1 && (
          <select
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B9970]"
            value={effectiveConnectionId ?? ''}
            onChange={(e) => setSelectedConnectionId(Number(e.target.value))}
            disabled={isLoading}
          >
            {connections.map((c) => (
              <option key={c.crmConnectionId} value={c.crmConnectionId}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <Button
          color={isConnected ? 'red' : 'teal'}
          variant="outlined"
          size="sm"
          onClick={() => {
            if (selectedConnection) {
              onDisconnect(selectedConnection);
            } else {
              onConnect();
            }
          }}
          disabled={isLoading || (isConnected && !selectedConnection)}
          className="rounded-lg font-bold"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <ClipLoader size={14} color={isConnected ? 'red' : '#2CA6A4'} />
              <span>Loading...</span>
            </div>
          ) : selectedConnection ? (
            `Disconnect ${CrmLabels[crmType]}`
          ) : (
            `Connect ${CrmLabels[crmType]}`
          )}
        </Button>
      </div>
    </div>
  );
};

const Account: FC<Props> = ({ timeZones, classicUrl }) => {
  const { user } = useUser();
  const userInfo = user as UserInfo;
  const [editPreferences, setEditPreferences] = useState<boolean>(false);
  const [show, setShow] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [subscriberInfo, setSubscriberInfo] = useState<SubscriberInfo | null>(
    null
  );
  const [isLoadingSubscriberInfo, setIsLoadingSubscriberInfo] = useState(false);
  const [connectionToDisconnect, setConnectionToDisconnect] =
    useState<CrmConnectionInfo | null>(null);
  const [crmTypeToConnect, setCrmTypeToConnect] = useState<CrmType | null>(
    null
  );
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [loadingCrmType, setLoadingCrmType] = useState<CrmType | null>(null);
  const [isSubscriberProfileExpanded, setIsSubscriberProfileExpanded] =
    useState(false);
  const [isAddressInformationExpanded, setIsAddressInformationExpanded] =
    useState(false);
  const [accountDetails, setAccountDetails] = useState<any>(
    userInfo?.accountDetails
  );

  const defaultTZ: TimeZone = {
    bclId: '',
    tzdbId: '',
  };

  const [selectedTimeZone, setSelectedTimeZone] = useState<TimeZone>(defaultTZ);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const { data: adminPreferences, mutate: mutatePreferences } =
    useSWR<UserPreferences>(
      userInfo?.sub ? `/api/admin/preferences/${userInfo.sub}` : null
    );

  const { data: adminAccountDetails, mutate: mutateAccountDetails } = useSWR(
    userInfo?.sub ? `/api/admin/accountdetails/${userInfo.sub}` : null
  );

  const { data: connectedCrmsData, mutate: mutateConnectedCrms } = useSWR<
    CrmConnectionInfo[]
  >(userInfo?.sub ? '/api/crm/connections' : null);
  const connectedCrms = connectedCrmsData ?? [];

  useEffect(() => {
    if (adminPreferences) {
      setInitialUserPreferences(adminPreferences);

      if (adminPreferences?.timeZone && Array.isArray(timeZones)) {
        const currentTZIndex = timeZones.findIndex(
          (tz) => tz.bclId === adminPreferences.timeZone
        );
        if (currentTZIndex !== -1) {
          setSelectedTimeZone(timeZones[currentTZIndex]);
        }
      }
    }
  }, [adminPreferences, timeZones]);

  useEffect(() => {
    if (adminAccountDetails) {
      setAccountDetails(adminAccountDetails);
    }
  }, [adminAccountDetails]);

  const fetchSubscriberInfo = async () => {
    setIsLoadingSubscriberInfo(true);
    try {
      const response = await fetch('/api/account/GetSubscriberInfo', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: SubscriberInfo = await response.json();
        setSubscriberInfo(data);
      } else {
        const errorBody = await response.text();
        Sentry.captureMessage(`Failed to fetch subscriber info: ${errorBody}`);
        console.error('Failed to fetch subscriber info');
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error fetching subscriber info:', error);
    } finally {
      setIsLoadingSubscriberInfo(false);
    }
  };

  const handleCrmDisconnect = async (connection: CrmConnectionInfo) => {
    const crmType = connection.crmConnectionType;
    setIsDisconnecting(true);
    try {
      const response = await fetch(
        `/api/crm/${connection.crmConnectionId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        setAlertMessage(`Successfully disconnected ${connection.name}`);
        setShow(true);
        setConnectionToDisconnect(null);
        await mutateConnectedCrms();
      } else {
        const errorBody = await response.text();
        Sentry.captureMessage(
          `Failed to disconnect ${CrmLabels[crmType]}: ${errorBody}`
        );
        alert(`Failed to disconnect from ${CrmLabels[crmType]}. Please try again.`);
      }
    } catch (error) {
      Sentry.captureException(error);
      alert(`Error disconnecting from ${CrmLabels[crmType]}. Please try again.`);
    } finally {
      setIsDisconnecting(false);
    }
  };

  useEffect(() => {
    fetchSubscriberInfo();
  }, []);
  const [initialUserPreferences, setInitialUserPreferences] =
    useState<UserPreferences>(
      userInfo?.userPreferences !== undefined
        ? userInfo.userPreferences
        : {
            userName: '',
            firstName: '',
            lastName: '',
            email: '',
            defaultLength: '',
            personalBiography: '',
            companyBiography: '',
            timeZone: '',
            personalNote: '',
            defaultSubject: '',
            crmEmail: '',
            pendingOffersFeed: '',
            confirmedOffersFeed: '',
          }
    );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [calendarName, setCalendarName] = useState('');

  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [externalCalendars, setExternalCalendars] = useState<
    CalendarListExternalCalendar[]
  >([]);

  const [calendars, setCalendars] = useState([
    {
      id: 1,
      name: 'Sample Calendar',
      provider: 'Google',
      displayEvents: false,
      edit: false,
      viewUrl: false,
      url: '',
    },
  ]);
  const [singleCalendar, setSingleCalendar] = useState<{
    id: number;
    name: string;
    provider: string;
    displayEvents: boolean;
    edit: boolean;
    viewUrl: boolean;
    url: string;
  } | null>(null);

  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState<File | null>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrmLogin = async (crmType: CrmType, name: string) => {
    setLoadingCrmType(crmType);
    try {
      const currentUrl = window.location.href;

      const request: CrmAuthRequest = { returnUrl: currentUrl, name };
      const response = await fetch(`/api/crm/auth/${CrmAuthSlugs[crmType]}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(
          error.message || `Failed to initiate ${CrmLabels[crmType]} login`
        );
      }

      const data: CrmAuthResponse = await response.json();

      if (data.loginUrl) {
        window.location.href = data.loginUrl;
      } else {
        await mutateConnectedCrms();
        setLoadingCrmType(null);
      }
    } catch (error) {
      Sentry.captureException(error);
      alert(`Error initiating ${CrmLabels[crmType]} login. Please try again.`);
      setLoadingCrmType(null);
    }
  };

  const refreshPage = () => {
    window.location.reload();
  };

  const handleOpenModal = () => {
    getExternalCalendars();
    fetchSubscriberInfo(); // Add this line
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCalendarName('');
  };

  const [isIcs, setIsIcs] = useState(false);
  const [calendarUrl, setCalendarUrl] = useState('');

  const saveIcs = async () => {
    try {
      console.log('Saving ICS calendar with URL:', calendarUrl);
      const data: AddExternalCalendarRequest = {
        name: calendarName,
        provider: 'Free/Busy',
        url: calendarUrl,
        accessToken: '',
        refreshToken: '',
      };

      const response = await fetch('/api/user/addExternalCalendar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error('Failed to save calendar');
      }

      const result = await response.json();
      console.log('Calendar added successfully:', result);

      setCalendarName('');
      setCalendarUrl('');
      setIsIcs(false);
      alert('Congratulations! Calendar created successfully!');
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error saving ICS calendar:', error);
      alert('Failed to save the calendar. Please try again.');
    }
  };

  const redirectHref = `${window.location.pathname}`;

  // Function to fetch external calendars from backend, convert them to CalendarListExterncalCalendar[],
  // and call setExternalCalendars()
  const getExternalCalendars = async () => {
    try {
      const response: Response = await fetch(
        '/api/user/externalCalendars/getExternalCalendars'
      );

      const responseObject: ExternalCalendar[] = await response.json();

      const calendars: CalendarListExternalCalendar[] = responseObject.map(
        (c) => ({
          id: c.id,
          name: c.name,
          provider: c.provider,
          excludedSchedulars: c.excludedCalendars,
          showOnSchedularPage: c.displayEvents,
          includeWithNewSchedulars: c.defaultReserve,
        })
      );

      calendars.sort((a, b) => {
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      });

      setExternalCalendars(calendars);
    } catch (error: any) {
      Sentry.captureException(error);
      console.error(
        'src/components/settings/account.tsx/getExterncalCalendars()',
        error
      );
    }
  };

  const handleMicrosoftSignIn = async () => {
    try {
      const tokens: Tokens | null = await getTokensFromNewMicrosoftSignIn();

      if (tokens) {
        // Build request
        const request: AddExternalCalendarRequest = {
          name: calendarName,
          provider: 'Microsoft',
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          url: '',
        };

        //call internal API
        await fetch('/api/user/addExternalCalendar', {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(request),
        });
      }

      getExternalCalendars(); //TODO: change so that only single calendar gets fetched
    } catch (error: any) {
      Sentry.captureException(error);
      console.error(
        'src/components/settings/account.tsx/handleMicrosoftSignIn',
        error
      );
    }
  };

  const edition = accountDetails?.edition;
  const mappedEditionName =
    edition !== undefined && edition !== null
      ? EditionLabels[Edition[edition] as keyof typeof EditionLabels]
      : 'Custom or Unknown';
  const showUltraUpgrade = canUpgradeToUltra(edition);
  const canUpgradeToSuperconnector =
    edition !== undefined &&
    edition !== null &&
    ![
      Edition.SuperConnector,
      Edition.Premium,
      Edition.Team,
    ].includes(edition);

  const handleGoogleSignIn = async () => {
    try {
      const tokens: Tokens | null = await getTokensFromNewGoogleSignIn();

      if (tokens) {
        // Build request
        const request: AddExternalCalendarRequest = {
          name: calendarName,
          provider: 'Google',
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          url: '',
        };

        //call internal API
        await fetch('/api/user/addExternalCalendar', {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(request),
        });
      }

      getExternalCalendars();
    } catch (error: any) {
      Sentry.captureException(error);
      console.error(
        'src/components/settings/account.tsx/handleMicrosoftSignIn',
        error
      );
    }
  };

  async function deleteAccount() {
    const result = await fetch('/api/account/delete');

    if (result.ok) {
      window.location.href = '/api/auth/logout?returnTo=/goodbye';
    } else {
      console.log(await result.text());
      alert('Unable to delete account, see console for error message.');
    }
  }

  console.log('initialUserPreferences', initialUserPreferences);
  return (
    <div className="flex flex-col w-full">
      {deleteModal && (
        <DeleteModal
          closeModal={() => setDeleteModal(false)}
          deleteAccount={deleteAccount}
        >
          <span className="text-xl font-bold break-words">
            Are you sure you want to delete this account?
          </span>
        </DeleteModal>
      )}

      <Formik
        enableReinitialize
        initialValues={{
          ...initialUserPreferences,
          company: subscriberInfo?.company || '',
          title: subscriberInfo?.title || '',
          cellPhone: subscriberInfo?.cellPhone || '',
          additionalPhone: subscriberInfo?.additionalPhone || '',
          address1: subscriberInfo?.address1 || '',
          address2: subscriberInfo?.address2 || '',
          city: subscriberInfo?.city || '',
          state: subscriberInfo?.state || '',
          postalCode: subscriberInfo?.postalCode || '',
          country: subscriberInfo?.country || '',
          linkedInUrl: extractLinkedInUsername(
            subscriberInfo?.linkedInUrl || ''
          ),
        }}
        onSubmit={async (values, { setSubmitting }) => {
          const userPreferences: UserPreferences = {
            userName: userInfo?.userPreferences?.userName || '',
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            defaultLength: values.defaultLength,
            personalBiography: initialUserPreferences?.personalBiography || '',
            companyBiography: initialUserPreferences?.companyBiography || '',
            timeZone: selectedTimeZone.bclId,
            personalNote: initialUserPreferences?.personalNote || '',
            defaultSubject: initialUserPreferences?.defaultSubject || '',
            crmEmail: values.crmEmail,
            pendingOffersFeed: initialUserPreferences?.pendingOffersFeed || '',
            confirmedOffersFeed:
              initialUserPreferences?.confirmedOffersFeed || '',
          };

          const subscriberData = {
            company: values.company,
            title: values.title,
            cellPhone: values.cellPhone,
            additionalPhone: values.additionalPhone,
            address1: values.address1,
            address2: values.address2,
            city: values.city,
            state: values.state,
            postalCode: values.postalCode,
            country: values.country,
            linkedInUrl: buildLinkedInUrl(values.linkedInUrl),
          };

          if (editPreferences) {
            try {
              const response = await fetch(
                `/api/user/${initialUserPreferences?.userName}/savePreferences`,
                {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    ...userPreferences,
                    ...subscriberData,
                  }),
                }
              );

              if (response.ok) {
                setAlertMessage('Account Details Saved');
                setShow(true);
                setEditPreferences(false);
                await mutatePreferences();
                await mutateAccountDetails();
                await fetchSubscriberInfo();
              } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save changes');
              }
              setSubmitting(false);
            } catch (err: any) {
              Sentry.captureException(err);
              console.error(err);
              alert(err.message || 'Error saving changes');
              setSubmitting(false);
            }
          }
        }}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="w-full">
            <div className="fixed top-4 right-4 z-50">
              <Toast
                show={show}
                setShow={setShow}
                alertMessage={alertMessage}
              />
            </div>

            {/* <div className="flex flex-col sm:flex-row px-4 py-4 sm:py-6 gap-4 border-b border-gray-100 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Photo</span>
              </div>
              <div className="flex-1 flex items-center space-x-6">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile Preview"
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-gray-100 shadow-sm"
                  />
                ) : (
                  <CgProfile className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400 border rounded-full" />
                )}
                <label className="cursor-pointer bg-gray-200 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-300">
                  Change Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              </div>
            </div> */}
            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Name</span>
              </div>
              <div className="flex-1">
                {editPreferences ? (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Field
                      as="input"
                      placeholder="First Name"
                      type="text"
                      name="firstName"
                      disabled={true}
                      className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all cursor-not-allowed text-gray-500"
                    />
                    <Field
                      as="input"
                      placeholder="Last Name"
                      type="text"
                      name="lastName"
                      disabled={true}
                      className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all cursor-not-allowed text-gray-500"
                    />
                  </div>
                ) : (
                  <span className="text-gray-900 font-medium">
                    {values.firstName} {values.lastName}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Email</span>
              </div>
              <div className="flex-1">
                {editPreferences ? (
                  <Field
                    as="input"
                    placeholder="Email"
                    type="text"
                    name="email"
                    disabled={true}
                    className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all cursor-not-allowed text-gray-500"
                  />
                ) : (
                  <span className="text-gray-900 font-medium break-all">
                    <EmailLink email={values.email} />
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Default Time Zone</span>
              </div>
              <div className="flex-1">
                {editPreferences ? (
                  <TZComboBox
                    options={timeZones}
                    selectedOption={selectedTimeZone}
                    setSelectedOption={setSelectedTimeZone}
                  />
                ) : (
                  <span className="text-gray-900 font-medium">
                    {values.timeZone}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Default Meeting Length</span>
              </div>
              <div className="flex-1 flex flex-col">
                <Select
                  label="Default Meeting Length"
                  color="teal"
                  value={values.defaultLength?.toString()}
                  onChange={(val) => setFieldValue('defaultLength', val)}
                  className="bg-white"
                  disabled={!editPreferences}
                >
                  {/* <Option value="00:00:00">Not Set / 0 Minutes</Option> */}
                  <Option value="00:15:00">15 Minutes</Option>
                  <Option value="00:30:00">30 Minutes</Option>
                  <Option value="00:45:00">45 Minutes</Option>
                  <Option value="01:00:00">60 Minutes</Option>
                  <Option value="01:30:00">90 Minutes</Option>
                  <Option value="02:00:00">120 Minutes</Option>
                </Select>

                <span className="text-xs text-gray-500 mt-1.5">
                  Default meeting length proposed on Custom Meeting Requests
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>Edition</span>
              </div>
              <div className="flex-1 flex flex-wrap items-center gap-4">
                <span className="font-medium">{mappedEditionName}</span>
                {canUpgradeToSuperconnector && (
                  <Button
                    size="sm"
                    className="bg-confirmed-secondary hover:bg-secondary-hover text-white py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all font-bold text-xs uppercase"
                    onClick={() => (window.location.href = '/pricing')}
                  >
                    {edition === Edition.MeetAndGreet
                      ? 'Upgrade to Superconnector'
                      : 'Upgrade Now'}
                  </Button>
                )}
                {showUltraUpgrade && (
                  <Button
                    size="sm"
                    className="bg-confirmed-secondary hover:bg-secondary-hover text-white py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all font-bold text-xs uppercase"
                    onClick={() =>
                      (window.location.href = getUltraCheckoutPath())
                    }
                  >
                    {ULTRA_UPGRADE_LABEL}
                  </Button>
                )}
              </div>
            </div>

            {accountDetails?.edition !== Edition.Team &&
              accountDetails?.edition !== Edition.Personal && (
                <div className="px-4 py-2 bg-blue-50/30 text-xs text-gray-600">
                  <p>
                    Your current edition expires in{' '}
                    {accountDetails?.daysLeftInSubscription} days and becomes
                    Personal Edition thereafter until upgraded.
                  </p>
                </div>
              )}

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left">
                <span>BCC Email</span>
              </div>
              <div className="flex-1">
                {editPreferences ? (
                  <Field
                    as="input"
                    placeholder="CRM BCC Email (ex: SalesForce)"
                    type="text"
                    name="crmEmail"
                    className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                  />
                ) : (
                  <span className="text-gray-900 font-medium break-all">
                    {values.crmEmail ? (
                      <EmailLink email={values.crmEmail} />
                    ) : (
                      'None'
                    )}
                  </span>
                )}
              </div>
            </div>

            <CrmConnectionTypeRow
              crmType={CrmType.Salesforce}
              connections={connectedCrms.filter(
                (c) => c.crmConnectionType === CrmType.Salesforce
              )}
              isLoading={loadingCrmType === CrmType.Salesforce}
              onConnect={() => setCrmTypeToConnect(CrmType.Salesforce)}
              onDisconnect={(connection) =>
                setConnectionToDisconnect(connection)
              }
            />
            <CrmConnectionTypeRow
              crmType={CrmType.HubSpot}
              connections={connectedCrms.filter(
                (c) => c.crmConnectionType === CrmType.HubSpot
              )}
              isLoading={loadingCrmType === CrmType.HubSpot}
              onConnect={() => setCrmTypeToConnect(CrmType.HubSpot)}
              onDisconnect={(connection) =>
                setConnectionToDisconnect(connection)
              }
            />

            <div className="flex flex-col sm:flex-row px-4 py-3 sm:py-4 gap-4 items-start sm:items-center">
              <div className="sm:w-1/4 font-bold text-gray-700 text-left" />
              <div className="flex-1 flex flex-wrap items-center gap-3">
                <Button
                  color="teal"
                  size="sm"
                  onClick={handleOpenModal}
                  variant="gradient"
                  className="rounded-lg font-bold"
                >
                  Connect Microsoft, Google, ics
                </Button>
              </div>
            </div>

            <hr className="border-t border-gray-200 my-4" />

            <Accordion
              open={isSubscriberProfileExpanded}
              className="mt-2 rounded-2xl bg-white"
              icon={<React.Fragment />}
            >
              <AccordionHeader
                onClick={() =>
                  setIsSubscriberProfileExpanded(!isSubscriberProfileExpanded)
                }
                className="p-4 bg-gray-50/50 hover:bg-gray-100 transition-colors border-b border-gray-100 transition-none"
              >
                <div className="flex items-center justify-between w-full">
                  <Typography
                    variant="h6"
                    color="blue-gray"
                    className="font-bold"
                  >
                    Subscriber Profile
                  </Typography>
                  <div
                    className={`transition-transform duration-300 ${
                      isSubscriberProfileExpanded ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDown size={20} />
                  </div>
                </div>
              </AccordionHeader>

              <AccordionBody
                className="p-0 !overflow-visible"
                key={editPreferences ? 'edit-subscriber' : 'view-subscriber'}
              >
                <div className="flex flex-col pb-24">
                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Company</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Company"
                          name="company"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.company}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Title</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Title"
                          name="title"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.title}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Cell Phone</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Cell Phone"
                          name="cellPhone"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.cellPhone ? (
                            <PhoneLink phone={values.cellPhone} />
                          ) : (
                            'None'
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Additional Phone</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Additional Phone"
                          name="additionalPhone"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.additionalPhone ? (
                            <PhoneLink phone={values.additionalPhone} />
                          ) : (
                            'None'
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>LinkedIn URL</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field name="linkedInUrl">
                          {({ field, form }: FieldProps) => (
                            <>
                              <div className="flex items-center border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus-within:border-confirmed-primary transition-all bg-white">
                                <span className="bg-gray-100 px-3 py-2 text-gray-500 border-r border-gray-100 text-sm select-none">
                                  linkedin.com/in/
                                </span>
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="your-profile"
                                  className="block w-full px-3 py-2 focus:ring-0 border-none text-sm outline-none"
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    const isLikelyPaste =
                                      val.toLowerCase().startsWith('http') ||
                                      (val.length - field.value.length > 8 &&
                                        isLinkedInUrl(val));

                                    if (isLikelyPaste) {
                                      form.setFieldValue(
                                        'linkedInUrl',
                                        extractLinkedInUsername(val)
                                      );
                                    } else {
                                      field.onChange(e);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    field.onBlur(e);
                                    const cleaned = extractLinkedInUsername(
                                      e.target.value
                                    );
                                    form.setFieldValue('linkedInUrl', cleaned);
                                  }}
                                />
                              </div>
                              {isLinkedInUrl(field.value) && (
                                <p className="mt-1.5 text-xs text-confirmed-secondary font-medium animate-fadeIn">
                                  Just enter your username; we've already
                                  included the base URL.
                                </p>
                              )}
                            </>
                          )}
                        </Field>
                      ) : (
                        <div className="flex items-center gap-2">
                          {values.linkedInUrl ? (
                            <a
                              href={values.linkedInUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-teal-600 hover:text-teal-700 flex items-center gap-1 font-medium break-all"
                            >
                              {values.linkedInUrl} <FiLink size={14} />
                            </a>
                          ) : (
                            <span className="italic text-gray-400">None</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionBody>
            </Accordion>

            <Accordion
              key={`address-${editPreferences}`}
              open={isAddressInformationExpanded}
              className="mt-4 rounded-2xl bg-white"
              icon={<React.Fragment />}
            >
              <AccordionHeader
                onClick={() =>
                  setIsAddressInformationExpanded(!isAddressInformationExpanded)
                }
                className="p-4 bg-gray-50/50 hover:bg-gray-100 transition-colors border-b border-gray-100 transition-none"
              >
                <div className="flex items-center justify-between w-full">
                  <Typography
                    variant="h6"
                    color="blue-gray"
                    className="font-bold"
                  >
                    Address Information
                  </Typography>
                  <div
                    className={`transition-transform duration-300 ${
                      isAddressInformationExpanded ? 'rotate-180' : ''
                    }`}
                  >
                    <ChevronDown size={20} />
                  </div>
                </div>
              </AccordionHeader>

              <AccordionBody className="p-0 overflow-visible">
                <div
                  className="flex flex-col pb-20"
                  key={editPreferences ? 'edit' : 'view'}
                >
                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Address Line 1</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Address Line 1"
                          name="address1"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.address1}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Address Line 2</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Address Line 2"
                          name="address2"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.address2}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>City</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="City"
                          name="city"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.city}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>State / Province</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="State / Province"
                          name="state"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.state}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 border-b border-gray-50 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Zip / Postal Code</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Zip / Postal Code"
                          name="postalCode"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.postalCode}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row px-4 py-3 sm:py-4 gap-4 items-center">
                    <div className="w-[45%] sm:w-1/4 font-bold text-gray-700 text-left shrink-0">
                      <span>Country</span>
                    </div>
                    <div className="flex-1">
                      {editPreferences ? (
                        <Field
                          as="input"
                          placeholder="Country"
                          name="country"
                          className="block w-full px-3 py-2 bg-white border-0 border-b-2 border-confirmed-secondary rounded-md shadow-sm focus:border-confirmed-primary focus:ring-0 transition-all mb-4"
                        />
                      ) : (
                        <span className="text-gray-900 font-medium">
                          {values.country}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionBody>
            </Accordion>

            {/* Modal */}
            <Dialog
              open={isModalOpen}
              handler={handleCloseModal}
              size="lg"
              className="z-50 max-w-[105vw] md:max-w-lg max-h-[90vh] min-w-[90%] md:min-w-auto"
            >
              {loading && (
                <div className="absolute top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
                  <div className="flex flex-col items-center">
                    <div className="loader border-t-4 border-blue-500 border-solid rounded-full w-16 h-16 animate-spin"></div>
                    <span className="text-white text-lg mt-4">
                      Loading Calendar
                    </span>
                  </div>
                </div>
              )}
              <DialogHeader className="text-lg sm:text-2xl font-medium flex-shrink-0 p-4">
                Connect/Disconnect Google and Microsoft Accounts
              </DialogHeader>
              <DialogBody className="overflow-y-auto max-h-[calc(90vh-140px)] flex-grow p-4">
                <div className="flex flex-col space-y-6 w-full">
                  <div className="flex flex-col space-y-4 w-full">
                    <div className="flex items-center gap-2 w-full">
                      <Typography
                        variant="h6"
                        className="flex flex-wrap items-center gap-2 w-full"
                      >
                        <span className="flex-shrink-0">
                          Step 1. Give your calendar a name
                        </span>
                        <div className="relative group inline-block">
                          <FaInfoCircle className="text-gray-500 cursor-pointer inline" />
                          <div className="absolute hidden group-hover:block bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-md top-full left-1/2 transform -translate-x-1/2 mt-2 md:w-max z-[1050] max-w-xs break-words w-auto">
                            This is how you will identify your calendar from
                            this account. Use a name that differentiates it from
                            other calendars/accounts that you connect.
                          </div>
                        </div>
                      </Typography>
                    </div>
                    <Input
                      label="Enter calendar name"
                      value={calendarName}
                      onChange={(e) => setCalendarName(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  {calendarName && (
                    <div className="flex flex-col space-y-4 w-full">
                      <div className="flex items-center gap-2 w-full">
                        <Typography
                          variant="h6"
                          className="flex items-start sm:items-center gap-2 w-full text-base sm:text-lg"
                        >
                          <span className="flex-shrink-0">
                            Step 2. Click the button of the provider you want to
                            connect to
                          </span>
                          <div className="relative group inline-block flex-shrink-0">
                            <FaInfoCircle className="text-gray-500 cursor-pointer" />
                            <div className="absolute hidden group-hover:block bg-gray-800 text-white text-sm px-2 py-1 rounded shadow-md top-full left-1/2 transform -translate-x-1/2 mt-2 max-w-xs break-words text-left z-[1050]">
                              You'll be taken to screens from your provider
                              requesting permissions for Confirmed. For the
                              system to work for you properly, you must allow
                              all requested permissions.
                            </div>
                          </div>
                        </Typography>
                      </div>
                      <div className="flex flex-col gap-4 w-full">
                        <Button
                          color="blue"
                          variant="outlined"
                          className="flex items-center justify-center gap-2 w-full"
                          onClick={handleGoogleSignIn}
                        >
                          <FcGoogle size={20} />
                          <span className="text-sm sm:text-base">
                            Sign in with Google
                          </span>
                        </Button>
                        <Button
                          color="blue"
                          variant="outlined"
                          className="flex items-center justify-center gap-2 w-full"
                          onClick={handleMicrosoftSignIn}
                        >
                          <FaMicrosoft size={20} className="text-[#0078D4]" />
                          <span className="text-sm sm:text-base">
                            Sign in with Microsoft
                          </span>
                        </Button>
                        <Button
                          color="blue"
                          variant="outlined"
                          className="flex items-center justify-center gap-2 w-full"
                          onClick={() => setIsIcs(true)}
                        >
                          <FiLink size={20} />
                          <span className="text-sm sm:text-base">
                            Connect with .ics
                          </span>
                        </Button>
                        {isIcs && (
                          <div className="mt-4">
                            <Typography
                              variant="h6"
                              className="mb-2 text-base sm:text-lg"
                            >
                              Step 3. Enter the URL of your Calendar
                            </Typography>
                            <Input
                              label="Enter url"
                              value={calendarUrl}
                              onChange={(e) => setCalendarUrl(e.target.value)}
                              className="w-full"
                            />
                            <div className="flex flex-col sm:flex-row justify-end mt-4 gap-2">
                              <Button
                                color="teal"
                                onClick={saveIcs}
                                className="w-full sm:w-auto"
                              >
                                Connect
                              </Button>
                              <Button
                                color="amber"
                                onClick={() => setIsIcs(false)}
                                className="w-full sm:w-auto"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        {refresh && !loading && (
                          <div className="flex flex-col sm:flex-row items-center gap-2 mt-4">
                            <label className="text-sm sm:text-base text-center sm:text-left">
                              Just added a calendar and don't see it yet?{'  '}
                              <i className="fa fa-arrow-right"></i>
                            </label>
                            <Button
                              color="amber"
                              onClick={refreshPage}
                              className="w-full sm:w-auto"
                            >
                              Refresh
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <Typography className="text-xs sm:text-sm w-full">
                    Need help connecting external calendars?{' '}
                    <a
                      href="https://youtu.be/yc_AC_xCuFM"
                      className="text-blue-500 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Watch this Video
                    </a>
                  </Typography>
                  <div className="list-group mt-4">
                    {externalCalendars.length === 0 ? (
                      <Typography
                        variant="h6"
                        className="mb-2 text-base sm:text-lg"
                      >
                        You Have No Connected Accounts
                      </Typography>
                    ) : (
                      <div>
                        <Typography
                          variant="h6"
                          className="mb-2 text-base sm:text-lg"
                        >
                          Connected Accounts
                        </Typography>
                        <div className="max-h-60 overflow-y-auto pr-2">
                          <CalendarList
                            externalCalendars={externalCalendars}
                            setExternalCalendars={setExternalCalendars}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter className="flex-shrink-0 p-4">
                <Button
                  color="amber"
                  onClick={handleCloseModal}
                  className="w-full sm:w-auto"
                >
                  Close
                </Button>
              </DialogFooter>
            </Dialog>

            {/* Hidden until we allow users to choose their flex cal (Scheduling Links) URL
      <div className="flex flex-row p-4">
        <div className="w-1/6 font-bold">
          <span>Personalized URL</span>
        </div>
        <div className="w-3/6">
          <span>
            confirmedapp.com/calendar/{userInfo?.userPreferences?.firstName}
            {userInfo?.userPreferences?.lastName}
          </span>
        </div>
        <div className="w-2/6 text-right text-blue-700">
          TODO: Edit
          <span>Edit</span>
        </div>
      </div> */}

            <div className="flex flex-col-reverse sm:flex-row p-4 gap-6 sm:justify-end items-stretch sm:items-center border-t border-gray-100 mt-6 pt-6">
              {!editPreferences ? (
                <Button
                  color="teal"
                  type="button"
                  onClick={() => setEditPreferences(true)}
                  className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl shadow-lg hover:shadow-teal-500/20 transition-all font-bold text-sm"
                >
                  <BiPencil size={18} />
                  <span>Edit Account Details</span>
                </Button>
              ) : (
                <>
                  <Button
                    variant="text"
                    color="gray"
                    type="button"
                    onClick={() => setEditPreferences(false)}
                    disabled={isSubmitting}
                    className="flex items-center justify-center gap-2 font-bold text-gray-600"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="teal"
                    disabled={isSubmitting}
                    className="flex flex-col sm:flex-row items-center justify-center gap-2 px-10 py-3 rounded-xl shadow-lg hover:shadow-teal-500/20 transition-all font-bold text-sm min-w-[200px]"
                  >
                    <div className="flex items-center gap-2">
                      <BiSave size={18} />
                      <span>
                        {isSubmitting ? 'Saving...' : 'Save All Changes'}
                      </span>
                    </div>
                    {!isSubmitting && (
                      <span className="text-[10px] font-normal opacity-80 block sm:inline">
                        (Reflected at next login)
                      </span>
                    )}
                  </Button>
                </>
              )}

              <Button
                type="button"
                onClick={() => setDeleteModal(true)}
                className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl shadow-lg hover:shadow-confirmed-red/20 transition-all font-bold text-sm bg-confirmed-red hover:bg-confirmed-red/80 text-white"
              >
                Delete Account
              </Button>
            </div>
          </Form>
        )}
      </Formik>

      {/* Disconnect CRM Confirmation Modal */}
      {connectionToDisconnect !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-[90vw] sm:max-w-md relative transform transition-all duration-300 ease-out">
            {/* Close button */}
            <button
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all duration-200"
              onClick={() => setConnectionToDisconnect(null)}
              disabled={isDisconnecting}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Icon */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-7 h-7 sm:w-8 sm:h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
                Disconnect {connectionToDisconnect.name}?
              </h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed px-2">
                Are you sure you want to disconnect{' '}
                {connectionToDisconnect.name}? You will no longer be able to
                sync contacts to{' '}
                {CrmLabels[connectionToDisconnect.crmConnectionType]} through
                this connection until you reconnect.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                onClick={() => setConnectionToDisconnect(null)}
                disabled={isDisconnecting}
              >
                Cancel
              </button>
              <button
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                onClick={() => handleCrmDisconnect(connectionToDisconnect)}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <CrmConnectionNameModal
        crmType={crmTypeToConnect}
        connectedCrms={connectedCrms}
        onCancel={() => setCrmTypeToConnect(null)}
        onConfirm={(name) => {
          if (crmTypeToConnect === null) return;
          const crmType = crmTypeToConnect;
          setCrmTypeToConnect(null);
          handleCrmLogin(crmType, name);
        }}
      />
    </div>
  );
};

export default Account;
